import React, { useState, useEffect } from "react";
import { Card, Tabs, Tab, Alert, Button, Modal, Form } from "react-bootstrap";
import PaymentMethodList from "./PaymentMethodList";
import SubscriptionStatus from "./SubscriptionStatus";
import { useSubscription } from "../hooks/useSubscription";
import { StripeProvider, CardElementForm } from "./stripe";
import {
  createSetupIntent,
  confirmPaymentMethod,
  getStripePublishableKey,
} from "../api";

/**
 * PaymentMethodManager Component
 * Issue #11: Payment Method Management
 * Updated for Issue #16: PCI Compliance
 *
 * Main component for comprehensive payment method management
 * with secure card handling using Stripe Elements
 */
const PaymentMethodManager = ({ className = "" }) => {
  const [activeTab, setActiveTab] = useState("list");
  const [showAddModal, setShowAddModal] = useState(false);
  const [setupIntent, setSetupIntent] = useState(null);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [error, setError] = useState(null);

  const {
    subscription,
    isAuthenticated,
    isAdmin,
    hasAccess,
    refreshSubscription,
  } = useSubscription();

  // Don't show for unauthenticated users
  if (!isAuthenticated) {
    return (
      <Alert variant="warning" className={className}>
        <i className="fas fa-sign-in-alt me-2"></i>
        Please log in to manage your payment methods.
      </Alert>
    );
  }

  // Admin users bypass payment method requirements
  if (isAdmin) {
    return (
      <Card className={className}>
        <Card.Body className="text-center py-4">
          <i
            className="fas fa-crown text-primary mb-3"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5>Admin Access</h5>
          <p className="text-muted">
            You have administrative access and don't need to manage payment
            methods.
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Check if user has subscription
  if (!subscription) {
    return (
      <Card className={className}>
        <Card.Body className="text-center py-4">
          <i
            className="fas fa-credit-card text-muted mb-3"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5>No Subscription Found</h5>
          <p className="text-muted">
            You need an active subscription to manage payment methods.
          </p>
          <SubscriptionStatus showDetails={true} showActions={true} />
        </Card.Body>
      </Card>
    );
  }

  const handleAddPaymentMethod = async () => {
    setShowAddModal(true);
    setError(null);

    try {
      const response = await createSetupIntent();
      if (response.data.success) {
        setSetupIntent(response.data.setup_intent);
      } else {
        throw new Error(
          response.data.message || "Failed to create setup intent"
        );
      }
    } catch (err) {
      console.error("Error creating setup intent:", err);
      setError(
        err.response?.data?.message ||
          "Failed to prepare payment method setup. Please try again."
      );
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setSetupIntent(null);
    setError(null);
    setAddingPaymentMethod(false);
  };

  const handleRefresh = () => {
    // This will trigger refresh in child components
    setError(null);
  };

  const handleSubmitPaymentMethod = async ({ setupIntent, setAsDefault }) => {
    if (!setupIntent) {
      setError("Setup intent is missing");
      return;
    }

    setAddingPaymentMethod(true);
    setError(null);

    try {
      const response = await confirmPaymentMethod(
        setupIntent.payment_method,
        setAsDefault
      );

      if (response.data.success) {
        // Successfully added payment method
        handleModalClose();
        await refreshSubscription();
      } else {
        throw new Error(
          response.data.message || "Failed to add payment method"
        );
      }
    } catch (err) {
      console.error("Error adding payment method:", err);
      setError(
        err.response?.data?.message ||
          "Failed to add payment method. Please try again."
      );
    } finally {
      setAddingPaymentMethod(false);
    }
  };

  const renderAddPaymentMethodContent = () => {
    if (!setupIntent) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Preparing secure payment form...</p>
        </div>
      );
    }

    return (
      <StripeProvider>
        <CardElementForm
          clientSecret={setupIntent.client_secret}
          setupIntentMode={true}
          onSubmit={handleSubmitPaymentMethod}
          buttonText="Add Payment Method"
          isLoading={addingPaymentMethod}
          error={error}
        />
      </StripeProvider>
    );
  };

  return (
    <div className={className}>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-credit-card me-2"></i>
              Payment Method Management
            </h5>
            <SubscriptionStatus compact={true} showActions={false} />
          </div>
        </Card.Header>

        <Card.Body>
          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setError(null)}
              className="mb-3"
            >
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            <Tab
              eventKey="list"
              title={
                <span>
                  <i className="fas fa-list me-1"></i>
                  My Payment Methods
                </span>
              }
            >
              <PaymentMethodList
                onAddPaymentMethod={handleAddPaymentMethod}
                onRefresh={handleRefresh}
              />
            </Tab>

            <Tab
              eventKey="subscription"
              title={
                <span>
                  <i className="fas fa-file-invoice-dollar me-1"></i>
                  Subscription Details
                </span>
              }
            >
              <div className="py-3">
                <SubscriptionStatus
                  showDetails={true}
                  showActions={true}
                  className="mb-4"
                />

                {subscription && (
                  <div className="row">
                    <div className="col-md-6">
                      <h6>
                        <i className="fas fa-info-circle me-2"></i>
                        Subscription Information
                      </h6>
                      <ul className="list-unstyled">
                        <li>
                          <strong>Status:</strong> {subscription.status_display}
                        </li>
                        <li>
                          <strong>Plan:</strong>{" "}
                          {subscription.plan?.name || "Unknown"}
                        </li>
                        {subscription.current_period_end && (
                          <li>
                            <strong>Next Billing:</strong>{" "}
                            {new Date(
                              subscription.current_period_end
                            ).toLocaleDateString()}
                          </li>
                        )}
                        {subscription.trial_end && (
                          <li>
                            <strong>Trial Ends:</strong>{" "}
                            {new Date(
                              subscription.trial_end
                            ).toLocaleDateString()}
                          </li>
                        )}
                      </ul>
                    </div>

                    <div className="col-md-6">
                      <h6>
                        <i className="fas fa-shield-alt me-2"></i>
                        Payment Security
                      </h6>
                      <ul className="list-unstyled">
                        <li>
                          <i className="fas fa-check text-success me-2"></i>
                          PCI DSS Compliant
                        </li>
                        <li>
                          <i className="fas fa-check text-success me-2"></i>
                          256-bit SSL Encryption
                        </li>
                        <li>
                          <i className="fas fa-check text-success me-2"></i>
                          Secure Card Storage
                        </li>
                        <li>
                          <i className="fas fa-check text-success me-2"></i>
                          Fraud Protection
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      {/* Add Payment Method Modal */}
      <Modal show={showAddModal} onHide={handleModalClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderAddPaymentMethodContent()}</Modal.Body>
      </Modal>
    </div>
  );
};

export default PaymentMethodManager;
